import { Link } from "@inertiajs/react";
import { CheckCircle2, X, School } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Notice } from "@/components/ui/notice";
import PrivacyConsentCheckbox from "@/components/forms/PrivacyConsentCheckbox";
import { formatRoom, formatGrade } from "@/lib/classroom";

/**
 * "Is this your child?" — the student an activation code matched, and
 * the button to add them. Used after the activation email and when a
 * signed-in parent adds another child.
 */
export default function ChildMatchCard({ student, form, onSubmit, cancelHref, requireConsent, consentId }) {
  const { data, setData, processing, errors } = form;
  const place = [formatRoom(student.classroom), student.grade && formatGrade(student.grade)].filter(Boolean).join(" · ");
  const school = [student.school, [student.school_city, student.school_province].filter(Boolean).join(", ")].filter(Boolean).join(" · ");

  return (
    <Card>
      <CardContent className="space-y-4 p-6 md:p-8">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <School className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="font-display text-xl font-semibold leading-tight text-foreground">{student.full_name}</p>
            {place && <p className="mt-0.5 text-sm text-muted-foreground">{place}</p>}
            {school && <p className="text-sm text-muted-foreground">{school}</p>}
          </div>
        </div>

        {student.already_linked && <Notice>You're already following {student.first_name}. Adding again changes nothing.</Notice>}

        <form onSubmit={onSubmit} className="space-y-4 pt-1">
          {requireConsent && (
            <PrivacyConsentCheckbox
              checked={data.privacy_consent}
              onChange={(checked) => setData("privacy_consent", checked)}
              error={errors.privacy_consent}
              id={consentId}
            />
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            <Button type="button" variant="ghost" asChild>
              <Link href={cancelHref}>
                <X className="h-4 w-4" /> Not my child
              </Link>
            </Button>
            <Button type="submit" disabled={processing || (requireConsent && !data.privacy_consent)}>
              <CheckCircle2 className="h-4 w-4" />
              {processing ? "Adding…" : `Yes, add ${student.first_name}`}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
